/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.core.intent

/**
 * Build a `wa.me` deep-link URL for a phone number + pre-filled message.
 *
 * The `+` (if present) is stripped; `wa.me` expects digits only.
 * The message is URL-encoded using the KMP-safe encoder below.
 *
 * @param phoneE164 phone in E.164 format, e.g. "+5491155550001".
 * @param message raw message text; will be URL-encoded.
 */
internal fun buildWhatsAppUrl(phoneE164: String, message: String): String {
    val digits = phoneE164.filter { it.isDigit() }
    val encoded = urlEncode(message)
    return "https://wa.me/$digits?text=$encoded"
}

/**
 * Minimal URL encoder that works across all KMP targets
 * (java.net.URLEncoder is not available on native/js).
 *
 * Preserves ASCII letters, digits, and `-_.~`. Encodes spaces as %20.
 * All other characters (including multi-byte UTF-8) are percent-encoded byte by byte.
 */
internal fun urlEncode(input: String): String {
    val sb = StringBuilder()
    for (ch in input) {
        when {
            ch.isLetterOrDigit() || ch in "-_.~" -> sb.append(ch)
            ch == ' ' -> sb.append("%20")
            else -> {
                val bytes = ch.toString().encodeToByteArray()
                for (b in bytes) {
                    sb.append('%')
                    sb.append(((b.toInt() ushr 4) and 0xF).toString(16).uppercase())
                    sb.append((b.toInt() and 0xF).toString(16).uppercase())
                }
            }
        }
    }
    return sb.toString()
}
